import { $createParagraphNode, $createTextNode, type ElementFormatType } from 'lexical';
import LatexNode from '../editor/nodes/LatexNode';
import SimpleLatexNode from '../editor/nodes/SimpleLatexNode';
import { $createImageNode } from '../editor/nodes/ImageNode';

/**
 * Parses a limited HTML string (with <p>, <span>, <latex>) and returns an array of Lexical nodes.
 * Only supports the subset of HTML the editor outputs.
 */
export function importHtmlToLexical(html: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<root>${html}</root>`, 'text/html');
    const root = doc.body.querySelector('root') || doc.body;
    const nodes: any[] = [];

    function parseElement(el: Element | ChildNode): any {
        if (el.nodeType === Node.TEXT_NODE) {
            const text = el.textContent ?? '';
            return text.trim() === '' ? null : $createTextNode(text);
        }

        if (!(el instanceof Element)) {
            return null;
        }

        switch (el.tagName.toLowerCase()) {
            case 'p': {
                const p = $createParagraphNode();

                // Handle alignment styles
                const textAlign = (el as HTMLElement).style.textAlign;
                const formatMap: Record<string, ElementFormatType> = {
                    left: 'left',
                    center: 'center',
                    right: 'right',
                    justify: 'justify',
                };
                if (textAlign && formatMap[textAlign]) {
                    p.setFormat(formatMap[textAlign]);
                }

                el.childNodes.forEach((child) => {
                    const parsed = parseElement(child);
                    if (parsed) p.append(parsed);
                });
                return p;
            }
            case 'span': {
                const textNode = $createTextNode(el.textContent || '');

                // Handle text styles based on class names
                const classList = el.classList;
                if (classList.contains('dynamic-bold')) {
                    textNode.toggleFormat('bold');
                }
                if (classList.contains('dynamic-italic')) {
                    textNode.toggleFormat('italic');
                }
                if (classList.contains('dynamic-underline')) {
                    textNode.toggleFormat('underline');
                }

                // Handle color
                const style = el.getAttribute('style') || '';
                const colorMatch = style.match(/color:\s*([^;]+)/);
                if (colorMatch) {
                    textNode.setStyle(`color: ${colorMatch[1]}`);
                }

                return textNode;
            }
            case 'latex': {
                const latex = el.textContent || '';
                const color = el.getAttribute('style')?.match(/color:\s*([^;]+)/)?.[1] || undefined;
                return color && color !== 'inherit'
                    ? new LatexNode(latex, color)
                    : new SimpleLatexNode(latex);
            }
            case 'img':
                return $createImageNode({
                    src: el.getAttribute('src') || '',
                    alt: el.getAttribute('alt') || '',
                    width: el.getAttribute('width') ? Number(el.getAttribute('width')) : undefined,
                    height: el.getAttribute('height')
                        ? Number(el.getAttribute('height'))
                        : undefined,
                });
            default:
                return $createTextNode(el.textContent || '');
        }
    }

    // Handle plain text input
    if (!html.trim().startsWith('<')) {
        const p = $createParagraphNode();
        p.append($createTextNode(html.trim()));
        return [p];
    }

    root.childNodes.forEach((child) => {
        const parsed = parseElement(child);
        if (parsed) nodes.push(parsed);
    });

    return nodes;
}
